import { useNavigate, useLocation } from 'react-router-dom'
import { useData } from '../../contexts/DataContext'
import { useFilters } from '../../contexts/FilterContext'
import { useView } from '../../contexts/ViewContext'

const iconMap = {
  map: '🗺️',
  grid: '⊞',
  chart: '📊',
  compare: '📈',
}

/**
 * SecondaryNav Component
 *
 * Secondary navigation bar below the header
 * Contains Category selector and View selector
 * Includes active filter count badge
 * Sticky positioning for easy access
 */
function SecondaryNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const { config } = useData()
  const { activeCategory, setCategory, filterCount } = useFilters()
  const { activeView, availableViews, setActiveView } = useView()

  const handleViewChange = viewId => {
    setActiveView(viewId)
    // Preserve existing query parameters when changing views
    const currentParams = new URLSearchParams(location.search)
    const newPath = `/dashboard/${viewId}${currentParams.toString() ? `?${currentParams.toString()}` : ''}`
    navigate(newPath)
  }

  if (!config?.facility_types || !config?.views) {
    return null
  }

  // Filter views to only show available ones for current category
  const filteredViews = config.views.filter(view =>
    availableViews?.includes(view.id)
  )

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm z-[900]">
      <div className="container mx-auto px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Category Selector */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700 hidden sm:inline">
              Category:
            </span>
            <div className="flex gap-2">
              {config.facility_types.map(type => (
                <button
                  key={type.id}
                  onClick={() => setCategory(type.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                    activeCategory === type.id
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {type.name}
                </button>
              ))}
            </div>
          </div>

          {/* View Selector with Filter Badge */}
          <div className="flex items-center gap-4">
            {/* Filter Count Badge */}
            {filterCount > 0 && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
                <span className="text-sm font-medium text-primary-700">
                  {filterCount} {filterCount === 1 ? 'filter' : 'filters'}{' '}
                  active
                </span>
              </div>
            )}

            {/* View Selector */}
            {filteredViews.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                  View:
                </span>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  {filteredViews.map(view => (
                    <button
                      key={view.id}
                      onClick={() => handleViewChange(view.id)}
                      className={`px-3 py-1.5 rounded-md font-medium transition-all text-sm flex items-center gap-1.5 ${
                        activeView === view.id
                          ? 'bg-white text-primary-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <span>{iconMap[view.icon] || '📄'}</span>
                      <span className="hidden sm:inline">{view.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Filter Count Badge */}
        {filterCount > 0 && (
          <div className="sm:hidden mt-2 flex items-center justify-center">
            <div className="px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-lg">
              <span className="text-xs font-medium text-primary-700">
                {filterCount} {filterCount === 1 ? 'filter' : 'filters'} active
              </span>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

export default SecondaryNav
